import { Track } from "@/src/lib/tracking";
import { authCacheManager } from "@features/auth/compat/auth-cache-compat";
import { LoginType } from "@features/auth/compat/sdk-types";
import type { IOspAuthModule } from "@features/auth/compat/sdk-types";
import { getItem } from "utils";
import { favoritesCenter } from "@store/favorite/FavoritesCenter";
import { LoginStore } from "@store/modules/login";
import { UserStore } from "@store/modules/user";
import { hasJustLoggedOutFlag } from "./authSessionSyncRefs";

async function syncSdkCredentialsFromOspCache(
  authModule: IOspAuthModule,
): Promise<void> {
  const cache = authCacheManager.getOspLoginCache();
  if (!cache?.accessToken) {
    return;
  }
  const stored = await authModule.storage.getCredentials();
  if (!stored?.accessToken) {
    await authModule.storage.setCredentials({
      accessToken: cache.accessToken,
      refreshToken: cache.refreshToken || "",
    });
  }
  if (cache.idToken) {
    try {
      await authModule.storage.setIdToken(cache.idToken);
    } catch (error) {
      Track.error(error, {
        tag: "syncAuthSessionFromSharedStorage",
        msg: "setIdToken skipped",
      });
    }
  }
}

/**
 * 从共享 localStorage / OSP 缓存恢复业务登录态（多 WebView 接收 `auth-session-updated` 时调用）。
 */
export async function syncAuthSessionFromSharedStorage(
  authModule?: IOspAuthModule | null,
): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }

  if (hasJustLoggedOutFlag()) {
    return false;
  }

  const ospLoginCache = authCacheManager.getOspLoginCache();
  if (!ospLoginCache?.accessToken) {
    return false;
  }

  if (
    LoginStore.isLoggedIn &&
    LoginStore.auth?.accessToken === ospLoginCache.accessToken
  ) {
    return true;
  }

  if (ospLoginCache.isRefreshAccessToken) {
    return false;
  }

  if (authModule) {
    try {
      await syncSdkCredentialsFromOspCache(authModule);
    } catch (error) {
      Track.error(error, {
        tag: "syncAuthSessionFromSharedStorage",
        msg: "sync SDK credentials failed",
      });
      return false;
    }
  }

  const ownerInfoCache = authCacheManager.getOwnerInfoCache(
    getItem("_user_id") || undefined,
  );

  UserStore.accessToken = ospLoginCache.accessToken;
  if (ownerInfoCache?.ownerInfo) {
    UserStore.fetchUserInfoFromCache();
  }

  LoginStore.loginComplete({
    loginType: ospLoginCache.loginType ?? (undefined as unknown as LoginType),
    eoaAddress:
      ospLoginCache.loginInfo?.eoaAddress ||
      ownerInfoCache?.ownerInfo?.eoaAddress ||
      null,
    auth: {
      accessToken: ospLoginCache.accessToken,
      refreshToken: ospLoginCache.refreshToken || "",
      idToken: ospLoginCache.idToken ?? "",
    },
  });

  favoritesCenter.setCurrentUserId(
    UserStore.ownerInfo?.id || ownerInfoCache?.ownerInfo?.id || "",
  );

  if (!ownerInfoCache?.ownerInfo) {
    void UserStore.fetchUserInfo("", true);
  }

  Track.log(
    "info",
    "syncAuthSessionFromSharedStorage",
    "session restored from cache",
    {
      loginType: ospLoginCache.loginType,
    },
  );
  return true;
}
